/**
 * portal — /portal/* (PLANNED RCP customer portal).
 * Function-key header + B2C Bearer token on every call.
 * Server scopes all reads/writes to authenticated `cifId` claim.
 */

import type {
  RequestOptions,
  ApiResult,
  GenericResult,
  PortalIdentityDto,
  StartApplicationPayload,
  ApplyStartResult,
  PersonDraft,
  ApplicationSubmitResult,
  PortalDocumentUpload,
  PortalDocumentRef,
  DownloadResult,
  AppointmentDto,
  RescheduleRequest,
  CustomerPreferencesDto,
  NotificationLogDto,
  StatusPeekDto,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

// ---------------------------------------------------------------------------
// Sub-APIs (matches teams-app-wiring §2.5 shape)
// ---------------------------------------------------------------------------

export class PortalIdentityApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public resolve(opts?: RequestOptions): Promise<ApiResult<PortalIdentityDto>> {
    return this.http.post<ApiResult<PortalIdentityDto>>('/portal/identity/resolve', {
      ...opts,
      body: {},
    });
  }
}

export class PortalApplyApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public start(
    req: StartApplicationPayload,
    opts?: RequestOptions
  ): Promise<ApiResult<ApplyStartResult>> {
    return this.http.post<ApiResult<ApplyStartResult>>('/portal/apply/start', {
      ...opts,
      body: req,
    });
  }

  public savePersonDraft(
    draftId: string,
    req: PersonDraft,
    opts?: RequestOptions
  ): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      `/portal/apply/save-person-draft/${encodeURIComponent(draftId)}`,
      { ...opts, body: req }
    );
  }

  public submit(
    draftId: string,
    opts?: RequestOptions
  ): Promise<ApiResult<ApplicationSubmitResult>> {
    return this.http.post<ApiResult<ApplicationSubmitResult>>(
      `/portal/apply/submit/${encodeURIComponent(draftId)}`,
      { ...opts, body: {} }
    );
  }
}

export class PortalDocumentsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public upload(
    req: PortalDocumentUpload,
    opts?: RequestOptions
  ): Promise<ApiResult<PortalDocumentRef>> {
    return this.http.post<ApiResult<PortalDocumentRef>>('/portal/documents/upload', {
      ...opts,
      body: req,
    });
  }

  public list(opts?: RequestOptions): Promise<ApiResult<ReadonlyArray<PortalDocumentRef>>> {
    return this.http.get<ApiResult<ReadonlyArray<PortalDocumentRef>>>('/portal/documents', {
      ...opts,
    });
  }

  public download(docId: string, opts?: RequestOptions): Promise<DownloadResult> {
    return this.http.get<DownloadResult>(
      `/portal/documents/${encodeURIComponent(docId)}`,
      { ...opts }
    );
  }
}

export class PortalAppointmentsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(opts?: RequestOptions): Promise<ApiResult<ReadonlyArray<AppointmentDto>>> {
    return this.http.get<ApiResult<ReadonlyArray<AppointmentDto>>>('/portal/appointments', {
      ...opts,
    });
  }

  public reschedule(
    appointmentId: string,
    req: RescheduleRequest,
    opts?: RequestOptions
  ): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      `/portal/appointments/reschedule/${encodeURIComponent(appointmentId)}`,
      { ...opts, body: req }
    );
  }

  public cancel(
    appointmentId: string,
    req: { reason?: string } = {},
    opts?: RequestOptions
  ): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      `/portal/appointments/cancel/${encodeURIComponent(appointmentId)}`,
      { ...opts, body: req }
    );
  }
}

export class PortalPreferencesApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public get(opts?: RequestOptions): Promise<ApiResult<CustomerPreferencesDto>> {
    return this.http.get<ApiResult<CustomerPreferencesDto>>('/portal/preferences', { ...opts });
  }

  public update(
    req: Partial<CustomerPreferencesDto>,
    opts?: RequestOptions
  ): Promise<GenericResult> {
    return this.http.patch<GenericResult>('/portal/preferences', { ...opts, body: req });
  }
}

export class PortalInboxApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(
    opts: RequestOptions & { top?: number; unreadOnly?: boolean } = {}
  ): Promise<ApiResult<ReadonlyArray<NotificationLogDto>>> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (opts.top !== undefined) query['top'] = opts.top;
    if (opts.unreadOnly !== undefined) query['unreadOnly'] = opts.unreadOnly;
    return this.http.get<ApiResult<ReadonlyArray<NotificationLogDto>>>('/portal/inbox', {
      ...opts,
      query,
    });
  }

  public markRead(notificationId: string, opts?: RequestOptions): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      `/portal/inbox/${encodeURIComponent(notificationId)}/read`,
      { ...opts, body: {} }
    );
  }
}

export class PortalStatusApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public peek(cartId: string, opts?: RequestOptions): Promise<ApiResult<StatusPeekDto>> {
    return this.http.get<ApiResult<StatusPeekDto>>(
      `/portal/status/${encodeURIComponent(cartId)}`,
      { ...opts }
    );
  }
}

// ---------------------------------------------------------------------------
// Aggregate
// ---------------------------------------------------------------------------

export class PortalApi {
  public readonly identity: PortalIdentityApi;
  public readonly apply: PortalApplyApi;
  public readonly documents: PortalDocumentsApi;
  public readonly appointments: PortalAppointmentsApi;
  public readonly preferences: PortalPreferencesApi;
  public readonly inbox: PortalInboxApi;
  public readonly status: PortalStatusApi;

  public constructor(http: ApiHttpClient) {
    this.identity = new PortalIdentityApi(http);
    this.apply = new PortalApplyApi(http);
    this.documents = new PortalDocumentsApi(http);
    this.appointments = new PortalAppointmentsApi(http);
    this.preferences = new PortalPreferencesApi(http);
    this.inbox = new PortalInboxApi(http);
    this.status = new PortalStatusApi(http);
  }
}
